<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :applied-options="control.appliedOptions.value"
    :error="control.control.value.errors"
  >
    <SectionWrapper id="notes" class="!mb-0">
      <template #header>
        <div
          v-if="isNoteInputShown || note"
          class="flex w-full items-center justify-between"
          :class="control.styles.control.label"
        >
          <h3
            v-if="
              isNoteInputShown ||
              note ||
              !checkTheme(
                'volvo',
                'tesla',
                'aa',
                'suzuki',
                'cinch',
                'crashdaddy',
                'jaguar',
                'jlr',
                'common',
                'referral',
                'landrover',
                'samsung'
              )
            "
          >
            {{ control.computedLabel.value }}
          </h3>
          <Transition name="slide-up">
            <div class="flex flex-row gap-3">
              <WButton
                v-if="note && !isNoteInputShown"
                type="ghost"
                :disabled="!control.control.value.enabled"
                :config="getClientTheme()"
                @click="showNoteInput"
              >
                <WidgetIcon :icon="noteOptions.editIcon" height="1.5rem" class="widget-action-icon" />
              </WButton>
              <WButton
                v-if="note && !isNoteInputShown"
                type="ghost"
                :disabled="!control.control.value.enabled"
                :config="getClientTheme()"
                @click="clearNote"
              >
                <WidgetIcon :icon="noteOptions.deleteIcon" height="1.5rem" class="widget-action-icon-negative" />
              </WButton>
            </div>
          </Transition>
        </div>
        <div v-else></div>
      </template>
      <div class="flex w-full flex-row flex-nowrap justify-start gap-5">
        <Transition mode="out-in" name="slide-up">
          <ActionCard
            v-if="!isNoteInputShown && !note"
            :title="noteOptions.title"
            :icon="noteOptions.icon"
            :content="noteOptions.description"
            :action="noteOptions.action"
            :disabled="!control.control.value.enabled"
            @action="showNoteInput"
          ></ActionCard>
          <div v-else-if="!note" class="w-full flex-nowrap">
            <WTextarea
              ref="textarea"
              v-model="input"
              name="note"
              autofocus
              :disabled="!control.control.value.enabled"
              @update:model-value="saveNote"
            />
          </div>
          <p class="body1 break-words" style="white-space: pre-line" v-else>{{ note }}</p>
        </Transition>
      </div>
    </SectionWrapper>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import ActionCard from 'components/base/ActionCard.vue';
  import SectionWrapper from 'components/claims-widget/components/SectionWrapper.vue';
  import WButton from 'components/base/Button.vue';
  import WTextarea from 'components/base/Textarea.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import { checkTheme, getClientTheme } from 'src/utils/config';

  export type NoteOptions = {
    title: string;
    description: string;
    action: string;
    icon: string;
    saveIcon?: string;
    editIcon?: string;
    deleteIcon?: string;
  };

  const props = defineProps(rendererProps<ControlElement>());
  const control = useWidgetControl(useJsonFormsControl(props));
  const noteOptions = ref((control.control.value.schema as unknown as { noteOptions: NoteOptions }).noteOptions);
  const note = ref((control.control.value.data || '') as string);
  const input = ref(note.value);
  const isNoteInputShown = ref(false);

  function showNoteInput() {
    isNoteInputShown.value = true;
    note.value = '';
  }

  function saveNote() {
    control.onChange(input.value);
  }

  function clearNote() {
    input.value = '';
    note.value = '';
    saveNote();
  }
</script>
